/**
 * Runtime top bar — app icon + title on the left, the user menu on the right.
 * Surface-agnostic: pass app identity + (optional) signed-in identity + api.
 * When anonymous, the right side shows an optional "Sign in" affordance.
 */
import type { ReactNode } from 'react'
import UserMenu, { type UserMenuItem, type UserMenuLook } from './UserMenu'
import type { AdminExtraTab } from './AdminConsole'
import type { ShellApi, ShellApp, ShellCapabilities, ShellIdentity, ShellNotify } from './types'

export default function RuntimeTopBar({
  app, identity, api, caps, onSignIn, onTitleClick, leftExtra, centerExtra, rightExtra, menuExtraItems, adminExtraTabs, notify,
  look,
}: {
  app: ShellApp
  identity: ShellIdentity | null
  api: ShellApi
  caps: ShellCapabilities
  onSignIn?: () => void
  onTitleClick?: () => void
  /** Nodes rendered on the left, straight after the app title (e.g. the app switcher). */
  leftExtra?: ReactNode
  /** Nodes in the bar's flexible middle (a layout's menus). */
  centerExtra?: ReactNode
  /** Appearance / Layout choices + the sign-out guard for the user menu. */
  look?: UserMenuLook
  /** Extra nodes rendered left of the user menu (e.g. nav links). */
  rightExtra?: ReactNode
  /** Extra entries injected into the user-menu dropdown. */
  menuExtraItems?: UserMenuItem[]
  /** Extra tabs injected into the System administration console. */
  adminExtraTabs?: AdminExtraTab[]
  /** Host toast adapter, threaded into the account/admin panels. */
  notify?: ShellNotify
}) {
  const accent = app.themeColor || '#4f46e5'
  return (
    /* THEME-AWARE, and it has to be: every control this bar hosts —
       Search, Approvals, the host's own `rightExtra` (the runtime's "Project
       Website" button) — paints itself from `--color-*`. The bar used to be a
       hard-coded white `rgba(255,255,255,0.95)` with `#0f172a` text, so in one
       theme or the other the bar and its contents were always going to
       disagree: in dark theme the buttons went dark-on-white, and in light
       theme the website button came out invisible. One background token for
       the bar and its contents is the only arrangement that works in both. */
    <header style={{
      // High z so the user-menu dropdown paints above embedded panes (the run
      // view's LivePreview / smart-page panes use their own stacked overlays).
      position: 'sticky', top: 0, zIndex: 9000, height: 56, flexShrink: 0,
      background: 'var(--color-bg-secondary, #ffffff)', backdropFilter: 'blur(8px)',
      borderBottom: '1px solid var(--color-border, #e2e8f0)',
      color: 'var(--color-text, #0f172a)',
      display: 'flex', alignItems: 'center',
      gap: 12, padding: '0 1rem',
    }}>
      <button type="button" onClick={onTitleClick} disabled={!onTitleClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none',
          cursor: onTitleClick ? 'pointer' : 'default', padding: 0, minWidth: 0,
        }}>
        <span style={{
          width: 32, height: 32, borderRadius: 9, background: accent, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <span className="material-icons" style={{ fontSize: 20, color: '#fff' }}>{app.icon || 'apps'}</span>
        </span>
        <span style={{
          fontWeight: 800, fontSize: '1.0625rem', color: 'var(--color-text, #0f172a)',
          whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '40vw',
        }}>{app.title || 'Application'}</span>
      </button>
      {leftExtra}
      {centerExtra && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>{centerExtra}</div>
      )}

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        {rightExtra}
        {identity ? (
          <UserMenu identity={identity} api={api} caps={caps} accentColor={accent} extraItems={menuExtraItems} adminExtraTabs={adminExtraTabs} notify={notify} look={look} />
        ) : onSignIn ? (
          <button type="button" onClick={onSignIn}
            style={{
              padding: '0.45rem 1rem', borderRadius: 999, fontWeight: 600, fontSize: '0.9125rem',
              background: accent, color: '#fff', border: 'none', cursor: 'pointer',
            }}>Sign in</button>
        ) : null}
      </div>
    </header>
  )
}
